import React, { useEffect } from "react";
import { Box, Button, CircularProgress, ThemeProvider, createTheme, ThemeOptions, Typography } from "@mui/material";
import type { Theme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setLoading, setBrandConfig } from "@store/slices/brandSlice";
import type { RootState } from "@store/store";
import { useLazyGetThemeConfigQuery } from "@api/catalogs";
import * as Sentry from "@sentry/react";
import { AppThemePalette, ThemeApiStructure } from "@interfaces/theme";
import { themeBase } from "@theme/_themeBase";
import { getBaseUrl } from "@helpers/funciones";
import { DEFAULT_PRIMARY, normalizeInterface, normalizePalette } from "@helpers/themeConfigHelpers";

const createDynamicTheme = (paletteConfig: AppThemePalette) => {
  // Neutros de la marca (`components.neutrals`) sobre los de Lendia; sin ellos quedan los del tema base.
  const neutrals = paletteConfig.components?.neutrals;
  const baseTokens = themeBase.palette?.tokens;
  const themeOptions: ThemeOptions = {
    palette: {
      ...(neutrals?.border && { divider: neutrals.border }),
      ...(baseTokens && {
        tokens: {
          ...baseTokens,
          ...(neutrals?.borderStrong && { borderStrong: neutrals.borderStrong }),
          ...(neutrals?.surface2 && { surface2: neutrals.surface2 }),
          ...(neutrals?.bg && { pageBg: neutrals.bg }),
        },
      }),
      primary: {
        main: paletteConfig.primary.main,
        hover: paletteConfig.primary.hover,
        dark: paletteConfig.primary.dark,
        light: paletteConfig.primary.light,
        base: paletteConfig.primary.base,
        accent: paletteConfig.primary.accent,
      },
      components: {
        slider: paletteConfig.components?.slider,
        progressBar: paletteConfig.components?.progressBar,
        buttonSelect: paletteConfig.components?.buttonSelect,
        uploadFile: paletteConfig.components?.uploadFile,
        expedienteButton: paletteConfig.components?.expedienteButton,
        sidebar: paletteConfig.components?.sidebar,
        text: paletteConfig.components?.text,
        neutrals,
      },
    },
  };
  return createTheme(themeBase, themeOptions);
};

interface DynamicThemeProviderProps {
  children: React.ReactNode;
  localTheme?: ThemeApiStructure;
}

export default function DynamicThemeProvider({ children, localTheme }: DynamicThemeProviderProps) {
  const dispatch = useDispatch();

  const { loading } = useSelector((state: RootState) => state.brand);

  const [getThemeConfig] = useLazyGetThemeConfigQuery();
  const [customTheme, setCustomTheme] = React.useState<Theme | null>(null);
  const [hasError, setHasError] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      setHasError(false);
      dispatch(setLoading(true));

      let fullUrl = window.location.href;
      if (fullUrl.includes("localhost")) {
        fullUrl = import.meta.env.VITE_LOCALHOST_THEME_URL;
      }

      if (!fullUrl) {
        throw new Error("Base URL is not valid");
      }

      const baseUrl = getBaseUrl(fullUrl);
      const response = localTheme ? localTheme : await getThemeConfig(baseUrl).unwrap();

      const { palette: newPalette, missingPrimary } = normalizePalette(response.palette.palette);
      const newInterface = normalizeInterface(response);

      // El registro de la base está incompleto: se completa con Lendia para no romper la pantalla.
      if (missingPrimary.length > 0) {
        Sentry.captureMessage("Theme config sin llaves de primary", {
          level: "warning",
          extra: { component: "DynamicThemeProvider-normalize", brand: response.name, missingPrimary },
        });
      }

      dispatch(
        setBrandConfig({
          id: response.id,
          name: response.name,
          code: response.code,
          palette: newPalette,
          interface: newInterface,
          display_name: response.display_name,
        }),
      );
      setCustomTheme(createDynamicTheme(newPalette));
    } catch (error) {
      console.error("Error fetching theme config", error);
      setHasError(true);
    } finally {
      dispatch(setLoading(false));
    }
  }, [getThemeConfig, dispatch, localTheme]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (hasError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 3,
          bgcolor: "#fff",
          px: 2,
        }}
      >
        <Typography variant="h1" fontWeight="700" color="#000">
          Oops!
        </Typography>
        <Typography variant="h4" fontWeight="bold" color="#000">
          Ocurrió un error al procesar la información
        </Typography>
        <Button
          variant="contained"
          onClick={fetchData}
          sx={{
            px: 3,
            py: 1.5,
          }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (loading || !customTheme) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress
          size={100}
          thickness={4}
          sx={{
            color: DEFAULT_PRIMARY.main,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: DEFAULT_PRIMARY.dark,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          Cargando...
        </Typography>
      </Box>
    );
  }

  return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
}
